import {
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useRedirectIfLoggedInHook } from "./useRedirectIfLoggedInHook";
import React, { useState } from "react";
import { FaGoogle } from "react-icons/fa";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Link,
  Stack,
  VStack,
  Divider,
  Text,
  Container,
  Heading,
  useToast,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { secureFetch } from "../data/secureFetch";

function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath =
    new URLSearchParams(location.search).get("redirect") || "/dashboard";

  // Handle input changes for email and password
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useRedirectIfLoggedInHook();

  // Handle email/password login submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuth();

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );
      const user = userCredential.user;

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Send ID token to backend for validation and JWT issuance
      const response = await secureFetch(`/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: { idToken },
      });

      if (!response.ok) throw new Error(response.message || "Failed to login");

      navigate(redirectPath);
      toast({
        title: "Login Successful",
        description: "You have successfully logged in.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      try {
        await signOut(auth);
      } catch {}
      toast({
        title: "Login Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Send ID token to backend for validation and login
      const response = await secureFetch(`/api/auth/google-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: { idToken }, // Send ID token to backend
      });

      console.log("login response: ", response);
      if (!response.ok)
        throw new Error(response.message || "Google login failed");

      navigate(redirectPath);
      toast({
        title: "Login Successful",
        description: "You have successfully logged in with Google.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      try {
        await signOut(auth);
      } catch {}
      toast({
        title: "Google Login Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Container maxW="lg" py={12} px={6} minH="60vh">
      <Box rounded="lg" boxShadow="lg" p={8} bg="white">
        <Helmet>
          <title>Sign In - PurelyGive</title>
          <meta
            name="description"
            content="Log in to your PurelyGive account to manage campaigns, track donations, and oversee your fundraising efforts without paying transaction fees."
          />
          <meta
            name="keywords"
            content="Sign In, sign in, fundraising platform, nonprofit account, PurelyGive"
          />
          <meta
            property="og:title"
            content="Sign In to PurelyGive - Manage Your Fundraising"
          />
          <meta
            property="og:description"
            content="Access your PurelyGive account to manage your nonprofit's fundraising campaigns and donations with no transaction fees."
          />
          <meta property="og:image" content={campaignImage} />
          <meta property="og:url" content="https://purelygive.com/login" />
        </Helmet>
        <Heading as="h2" size="xl" mb={6} textAlign="center">
          Sign In
        </Heading>

        <VStack spacing={6}>
          {/* Email/Password Form */}
          <Box p={6} width="100%">
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </FormControl>
                <Button mt={4} colorScheme="orange" type="submit" w="full">
                  Sign In
                </Button>
              </Stack>
            </form>
          </Box>

          {/* Horizontal Line with OR */}
          <Box display="flex" alignItems="center" width="100%">
            <Divider />
            <Text
              as="span"
              px={2}
              color="gray.500"
              fontWeight="bold"
              fontSize="sm"
            >
              OR
            </Text>
            <Divider />
          </Box>

          {/* Google Sign In Button */}
          <Box p={6} width="100%">
            <Button
              colorScheme="blue"
              onClick={handleGoogleLogin}
              leftIcon={<FaGoogle />}
              w="full"
            >
              Sign in with Google
            </Button>
          </Box>
        </VStack>
      </Box>
      <Text p={5} textAlign="center">
        Don't have an account?{" "}
        <Link as={RouterLink} to="/register" color="blue.500">
          Register here!
        </Link>
      </Text>
    </Container>
  );
}

export default Login;
